import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import {
  isTciManagerForInstitution,
  loadManagedDomains,
} from "services/fde/tciManagerService";
import { db } from "services/firebase";

const CandidateQueuePage = () => {
  const navigate = useNavigate();
  const { user, organizationId, userData, isGlobalAdmin } = useAuth();
  const { isFdeDc } = useFdeRole();
  const canOpenReview = isGlobalAdmin || isFdeDc;
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  const loadCandidates = useCallback(async () => {
    if (!institutionId || !user?.uid) {
      setLoading(false);
      return;
    }

    const canReview =
      isGlobalAdmin ||
      isFdeDc ||
      userData?.role === "admin" ||
      (await isTciManagerForInstitution({ uid: user.uid, institutionId }));

    setAllowed(canReview);
    if (!canReview) {
      setLoading(false);
      return;
    }

    let managedTciIds = [];
    if (!isGlobalAdmin && isFdeDc) {
      const domains = await loadManagedDomains({ uid: user.uid, institutionId });
      managedTciIds = [
        ...new Set(domains.flatMap((domain) => domain.tciIds || []).filter(Boolean)),
      ];
    }

    const snap = await db
      .collection("promptCandidates")
      .where("organizationId", "==", institutionId)
      .where("status", "==", "pending")
      .get();

    const rows = snap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((row) => {
        if (isGlobalAdmin || userData?.role === "admin") return true;
        return !row.tciId || managedTciIds.includes(row.tciId);
      });

    setCandidates(rows);
    setLoading(false);
  }, [institutionId, user?.uid, isGlobalAdmin, isFdeDc, userData?.role]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  if (loading) return null;

  if (!allowed) {
    return (
      <Alert severity="warning">
        TCI Manager or org admin access required to review prompt candidates.
      </Alert>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Prompt candidates"
        subtitle="Pending prompt revisions from your team's clinical feedback."
      />
      {isFdeDc && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Use the three-panel review from{" "}
          <Typography component={RouterLink} to="/dashboard/tci-manager" color="primary">
            TCI Manager
          </Typography>{" "}
          for promote, reject, and return actions.
        </Alert>
      )}
      {!canOpenReview && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Oversight view only. Promotion, rejection, and return actions require a TCI
          Manager.
        </Alert>
      )}
      <Stack spacing={2}>
        {candidates.map((candidate) => (
          <Paper key={candidate.id} sx={{ p: 2 }}>
            <Typography variant="subtitle1">{candidate.promptId}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {candidate.diffSummary}
            </Typography>
            {canOpenReview ? (
              <LoadingButton
                variant="contained"
                onClick={() =>
                  navigate(`/dashboard/tci-manager/candidates/${candidate.id}`)
                }
              >
                Open review
              </LoadingButton>
            ) : (
              <Typography variant="caption" color="text.secondary" display="block">
                Awaiting TCI Manager review for promotion.
              </Typography>
            )}
          </Paper>
        ))}
        {!candidates.length && (
          <Typography color="text.secondary">No pending candidates.</Typography>
        )}
      </Stack>
    </Box>
  );
};

export default CandidateQueuePage;
